/**
 * Common contact form setup/processing
 */
import {
  type ContactFormType,
  type InternalFormDataSchema,
  type SchemaForFormType,
} from "@pph-webflow/contact-form-handler";

import { loadValuesIntoForm, showErrorsForForm, markRequiredInputs } from "./utils/forms";
import { getQueryParams } from "./utils/getQueryParams";

export const DEFAULT_FORM_TYPE_KEY = "formType";
export const DEFAULT_FORM_NAME_KEY = "formName";
export const DEFAULT_NEXT_URL_KEY = "nextUrl";

export type DefaultFormValueKeys = {
  [DEFAULT_FORM_NAME_KEY]: string;
  [DEFAULT_NEXT_URL_KEY]: string;
};

const DEFAULT_ERROR_PARAM_SUFFIX = "_error";

export const DEFAULT_FORM_NAME_SELECTOR = "#form-name";

const DEFAULT_FORM_CONTAINER_SELECTOR = "#form-container";
const DEFAULT_FORM_SUCCESS_BLOCK_SELECTOR = "#form-success";
const DEFAULT_FORM_ERROR_BLOCK_SELECTOR = "#form-error";

type BooleanString = "false" | "true";

export type CommonFormValues = {
  formName: string;
  formType: ContactFormType;
  utmTag?: string;
  success?: BooleanString;
  errors?: "true";
};

export type AttachToFormConfig<FormValues extends InternalFormDataSchema> = {
  defaultFormValues?: Partial<FormValues>;
  errorParamSuffix?: string;
  formContainerSelector?: string;
  formErrorBlockSelector?: string;
  formNameKey?: keyof FormValues;
  formNameSelector?: string;
  formSuccessBlockSelector?: string;
  formTypeKey?: keyof FormValues;
  nextUrlKey?: keyof FormValues;
  selectOptionFallbackValues?: Record<string, string>;
  disabledSubmitBtnTimeoutMs?: number;
  disabledSubmitBtnText?: string;
};

export function attachToForm<T extends ContactFormType>(
  formType: T,
  {
    defaultFormValues = {},
    disabledSubmitBtnText = "Submitting…",
    disabledSubmitBtnTimeoutMs = 5000,
    errorParamSuffix = DEFAULT_ERROR_PARAM_SUFFIX,
    formContainerSelector = DEFAULT_FORM_CONTAINER_SELECTOR,
    formErrorBlockSelector = DEFAULT_FORM_ERROR_BLOCK_SELECTOR,
    formNameSelector = DEFAULT_FORM_NAME_SELECTOR,
    formSuccessBlockSelector = DEFAULT_FORM_SUCCESS_BLOCK_SELECTOR,
    selectOptionFallbackValues = {},
  }: AttachToFormConfig<SchemaForFormType<T>>
) {
  try {
    const $formContainer = $(formContainerSelector);
    const $form: JQuery<HTMLFormElement> = $formContainer.find("form");

    if (!$form[0]) {
      console.warn("Form not found on this page.");
      return;
    }

    markRequiredInputs($("input[required]"));

    const params = getQueryParams<CommonFormValues & SchemaForFormType<T>>();

    if ("errors" in params && params.errors === "true") {
      const formErrors = Object.fromEntries<string>(
        Object.entries(params)
          // We don't want to process errors that are not a string
          // And we'll ignore the "errors" flag
          .filter(([key, value]) => {
            return key !== "errors" && key.endsWith(errorParamSuffix) && typeof value === "string";
          })
          // Remove the suffix from the error key so we can append to
          // the correct field
          .map(([key, value]) => [key.replace(errorParamSuffix, ""), value])
      );

      $formContainer[0].scrollIntoView();
      showErrorsForForm($form, formErrors);
    } else if ("success" in params) {
      $formContainer[0].scrollIntoView();

      // For redirects that include the success flag, we will remove the form,
      // toggle the proper status block, and ensure that the status block height
      // matches the form to reduce bounces

      const formStatusSelector =
        params.success === "true" ? formSuccessBlockSelector : formErrorBlockSelector;
      const formStatusEl = $formContainer.find(formStatusSelector).first();

      // remove the form to prevent re-submission
      $form.detach();

      formStatusEl.css("display", "block");
      return;
    }

    try {
      const $formNameEl = $(formNameSelector);

      const preloadedValues = {
        formType,
        formName: $formNameEl.first().text(),
      };

      // Load up our queryParams into the form
      loadValuesIntoForm(
        $form,
        {
          ...preloadedValues,
          ...defaultFormValues,
          ...params,
        },
        selectOptionFallbackValues
      );

      // attach onSubmit listener so we can disable the submit button. Hopefully this
      // helps with duplicate submissions
      $form.on("submit", null, function () {
        const $submitBtn = $form.children("[type=submit]");

        // save original text so we can put it back
        const $submitBtnText = $submitBtn.text();

        $submitBtn.css({ opacity: 0.25 });
        $submitBtn.prop("disabled", true);
        $submitBtn.val(disabledSubmitBtnText);

        setTimeout(() => {
          // re-enable the button if we still haven't gotten a response/redirect
          $submitBtn.css({ opacity: 1 });
          $submitBtn.prop("disabled", false);
          $submitBtn.val($submitBtnText);
        }, disabledSubmitBtnTimeoutMs);
      });
    } catch (err) {
      throw new Error(
        `Unable to load form values: ${err instanceof Error ? err.message : "unknown error"}`
      );
    }
  } catch (err) {
    console.error(
      "Encountered an error while attaching to form",
      err instanceof Error && err.message
    );
  }
}
